import {useQuery} from '@tanstack/react-query'
import {useEffect, useState} from 'react'
import {Socket} from 'socket.io-client'
import {socketService} from 'src/services/api/scoket-service'
import storage from 'src/services/storage'
import {FaceDetectionWSChannelEnum, WebSocketRoomTypeEnum} from 'src/utilities/enums/WSChannelEnums'

const FIVE_SECONDS_IN_MILLISECONDS = 5000

export enum WebSocketStatusesEnum {
  CONNECTED = 'Conectado',
  DISCONNECTED = 'Desconectado',
  RECONNECTING = 'Reconectando',
}

type Props = {
  listenTo?: Array<FaceDetectionWSChannelEnum>
  room: WebSocketRoomTypeEnum
  onDisconnect?: () => void
  onConnect?: () => void
  onMessage?: (data: any) => void
  enable?: boolean
}

/**
 * Connects to websocket automatically and listens to the provided channels.
 * @param onConnect - Callback to be called when the websocket connects.
 * @param onDisconnect - Callback to be called when the websocket disconnects.
 * @param onMessage - Callback to be called when a message is received.
 * @param listenTo - Array of channels to listen to.
 * @param room - Room to connect to.
 */
export default function useWebsocket({listenTo, room, onDisconnect, onConnect, onMessage, enable = true}: Props) {
  const [socket, setSocket] = useState<Socket | null>(null)
  const [webSocketConnectionStatus, setWebSocketConnectionStatus] = useState<WebSocketStatusesEnum>(WebSocketStatusesEnum.RECONNECTING)

  const connectWS = async () => {
    try {
      if (!enable) return false
      return new Promise<boolean>((resolve, reject) => {
        const socketConnection = socketService({
          authorization: `Bearer ${storage.get('token')}`,
        })

        socketConnection.on('connect', () => {
          if (webSocketConnectionStatus === WebSocketStatusesEnum.DISCONNECTED || !enable) reject('Socket disconnected by state, rejecting connection')
          socketConnection.emit('room', room)
          resolve(true)
          setSocket(socketConnection)
          setWebSocketConnectionStatus(WebSocketStatusesEnum.CONNECTED)
          onConnect?.()
        })

        socketConnection.on('connect_error', () => {
          resolve(false)
          setSocket(null)
          setWebSocketConnectionStatus(WebSocketStatusesEnum.RECONNECTING)
        })

        socketConnection.on('disconnect', async () => {
          if (webSocketConnectionStatus === WebSocketStatusesEnum.DISCONNECTED) return
          setSocket(null)
          setWebSocketConnectionStatus(WebSocketStatusesEnum.RECONNECTING)
          onDisconnect?.()
        })
        return socketConnection
      })
    } catch (error) {
      console.error(`Failed to connect to websocket ${error}`)
      return false
    }
  }

  const disconnectWS = () => {
    socket?.disconnect()
    setWebSocketConnectionStatus(WebSocketStatusesEnum.DISCONNECTED)
    setSocket(null)
  }

  useEffect(() => {
    if (!enable) disconnectWS()
  }, [enable])

  useQuery({
    retry: 0,
    refetchOnWindowFocus: false,
    queryKey: [room],
    queryFn: async () => !socket && (await connectWS()),
    enabled: enable,
  })

  useEffect(() => {
    if (socket && listenTo) {
      for (const channel of listenTo) {
        socket.on(channel, (e) => onMessage?.(e))
      }
    }
  }, [socket, onMessage])

  useEffect(() => {
    if (webSocketConnectionStatus === WebSocketStatusesEnum.RECONNECTING) {
      const reconnectInterval = setTimeout(async () => {
        if (!socket) await connectWS()
      }, FIVE_SECONDS_IN_MILLISECONDS)

      return () => clearTimeout(reconnectInterval)
    }
  }, [webSocketConnectionStatus])

  return {socket, webSocketConnectionStatus, forceConnect: connectWS, forceDisconnect: disconnectWS}
}
